import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

interface InsightPoint {
  icon: string;
  text: string;
}

interface MindsetGrowthProps {
  category: string;
  quote: string;
  author: string;
  authorTitle: string;
  insights: InsightPoint[];
  reflection: string;
  accentGradient?: [string, string];
}

const DEFAULT_PROPS: MindsetGrowthProps = {
  category: '认知升级',
  quote: '你的认知边界，就是你的世界边界。突破认知，才能突破人生。',
  author: '查理·芒格',
  authorTitle: '伯克希尔·哈撒韦副董事长',
  insights: [
    { icon: '🔑', text: '多元思维模型：用不同学科的视角看同一个问题' },
    { icon: '🪞', text: '反向思考：想知道如何成功，先研究如何失败' },
    { icon: '📚', text: '终身学习：每天睡前都要比醒来时聪明一点' },
  ],
  reflection: '今天，你突破了哪一个思维边界？',
  accentGradient: ['#c084fc', '#818cf8'],
};

export const MindsetGrowthTemplate: React.FC<Partial<MindsetGrowthProps>> = (props) => {
  const p = { ...DEFAULT_PROPS, ...props };
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const [accent1, accent2] = p.accentGradient!;

  // Breathing ring animation
  const breathe = interpolate(Math.sin(frame / 25), [-1, 1], [0.92, 1.08]);
  const breatheOpacity = interpolate(Math.sin(frame / 25), [-1, 1], [0.15, 0.35]);

  // Phase 1: Category + Quote (0-120)
  const categoryOpacity = interpolate(frame, [0, 25], [0, 1], { extrapolateRight: 'clamp' });
  const quoteChars = p.quote.length;
  const quoteVisible = Math.floor(interpolate(frame, [20, 20 + quoteChars * 2], [0, quoteChars], { extrapolateRight: 'clamp' }));

  // Phase 2: Author (120-160)
  const authorOpacity = interpolate(frame, [120, 145], [0, 1], { extrapolateRight: 'clamp' });
  const authorY = spring({ frame: frame - 120, fps, from: 20, to: 0, config: { damping: 20, stiffness: 100 } });

  // Phase 3: Insights (170-350)
  const insightStart = 170;
  const insightInterval = 55;

  // Phase 4: Reflection (370-430)
  const reflectionOpacity = interpolate(frame, [370, 400], [0, 1], { extrapolateRight: 'clamp' });
  const reflectionScale = spring({
    frame: frame - 370,
    fps,
    from: 0.9,
    to: 1,
    config: { damping: 14, stiffness: 100, mass: 0.8 },
  });

  return (
    <AbsoluteFill
      style={{
        background: 'linear-gradient(180deg, #0c0a1d 0%, #1a1333 50%, #0f0b2a 100%)',
        fontFamily: '-apple-system, "PingFang SC", "Noto Serif SC", serif',
        color: 'white',
        overflow: 'hidden',
      }}
    >
      {/* Breathing circle decoration */}
      <div
        style={{
          position: 'absolute',
          top: '15%',
          left: '50%',
          width: 500,
          height: 500,
          transform: `translate(-50%, -50%) scale(${breathe})`,
          borderRadius: '50%',
          border: `2px solid rgba(192, 132, 252, ${breatheOpacity})`,
        }}
      />
      <div
        style={{
          position: 'absolute',
          top: '15%',
          left: '50%',
          width: 380,
          height: 380,
          transform: `translate(-50%, -50%) scale(${1.16 - (breathe - 0.92)})`,
          borderRadius: '50%',
          border: `1px solid rgba(129, 140, 248, ${breatheOpacity * 0.7})`,
        }}
      />

      {/* Subtle stars */}
      {Array.from({ length: 20 }).map((_, i) => {
        const x = (i * 61 + 17) % 1080;
        const y = (i * 89 + 33) % 1920;
        const twinkle = interpolate(Math.sin((frame + i * 15) / 20), [-1, 1], [0.1, 0.6]);
        return (
          <div
            key={i}
            style={{
              position: 'absolute',
              left: x,
              top: y,
              width: 3,
              height: 3,
              borderRadius: '50%',
              background: 'white',
              opacity: twinkle,
            }}
          />
        );
      })}

      {/* Category tag */}
      <div
        style={{
          position: 'absolute',
          top: 130,
          left: 0,
          right: 0,
          textAlign: 'center',
          opacity: categoryOpacity,
        }}
      >
        <div
          style={{
            display: 'inline-block',
            padding: '10px 32px',
            background: `linear-gradient(135deg, ${accent1}20, ${accent2}20)`,
            border: `1px solid ${accent1}40`,
            borderRadius: 50,
            fontSize: 40,
            color: accent1,
            fontWeight: 600,
            letterSpacing: '0.15em',
          }}
        >
          {p.category}
        </div>
      </div>

      {/* Quote */}
      <div
        style={{
          position: 'absolute',
          top: 260,
          left: 80,
          right: 80,
          textAlign: 'center',
        }}
      >
        {/* Opening quote mark */}
        <div
          style={{
            fontSize: 100,
            color: `${accent1}30`,
            fontFamily: 'Georgia, serif',
            lineHeight: 0.6,
            marginBottom: -20,
          }}
        >
          "
        </div>
        <div
          style={{
            fontSize: 48,
            fontWeight: 600,
            lineHeight: 1.7,
            color: 'rgba(255,255,255,0.95)',
            letterSpacing: '0.02em',
            minHeight: 250,
          }}
        >
          {p.quote.substring(0, quoteVisible)}
          {quoteVisible < quoteChars && (
            <span
              style={{
                opacity: interpolate(frame % 20, [0, 10, 20], [1, 0, 1]),
                color: accent1,
              }}
            >
              |
            </span>
          )}
        </div>
      </div>

      {/* Author */}
      <div
        style={{
          position: 'absolute',
          top: 650,
          left: 0,
          right: 0,
          textAlign: 'center',
          opacity: authorOpacity,
          transform: `translateY(${authorY}px)`,
        }}
      >
        <div
          style={{
            width: 100,
            height: 2,
            background: `linear-gradient(90deg, transparent, ${accent1}, transparent)`,
            margin: '0 auto 20px',
          }}
        />
        <div style={{ fontSize: 40, fontWeight: 700, color: accent1 }}>—— {p.author}</div>
        <div style={{ fontSize: 36, color: 'rgba(255,255,255,0.4)', marginTop: 8 }}>{p.authorTitle}</div>
      </div>

      {/* Insights */}
      <div
        style={{
          position: 'absolute',
          top: 920,
          left: 60,
          right: 60,
          display: 'flex',
          flexDirection: 'column',
          gap: 20,
        }}
      >
        <div style={{ fontSize: 44, color: 'rgba(255,255,255,0.35)', marginBottom: 8, fontWeight: 500 }}>
          核心洞察
        </div>
        {p.insights.map((insight, i) => {
          const startFrame = insightStart + i * insightInterval;
          const progress = spring({
            frame: frame - startFrame,
            fps,
            from: 0,
            to: 1,
            config: { damping: 16, stiffness: 120, mass: 0.6 },
          });
          const opacity = interpolate(frame, [startFrame, startFrame + 15], [0, 1], { extrapolateRight: 'clamp' });

          return (
            <div
              key={i}
              style={{
                opacity,
                transform: `translateX(${(1 - progress) * 50}px)`,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 16,
                  padding: '22px 28px',
                  background: 'rgba(255,255,255,0.04)',
                  borderRadius: 18,
                  border: '1px solid rgba(255,255,255,0.06)',
                  backdropFilter: 'blur(8px)',
                }}
              >
                <span style={{ fontSize: 40, flexShrink: 0 }}>{insight.icon}</span>
                <span style={{ fontSize: 38, lineHeight: 1.6, color: 'rgba(255,255,255,0.8)' }}>
                  {insight.text}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Reflection prompt */}
      <div
        style={{
          position: 'absolute',
          bottom: 100,
          left: 60,
          right: 60,
          textAlign: 'center',
          opacity: reflectionOpacity,
          transform: `scale(${reflectionScale})`,
        }}
      >
        <div
          style={{
            display: 'inline-block',
            padding: '20px 40px',
            background: `linear-gradient(135deg, ${accent1}15, ${accent2}15)`,
            border: `1px solid ${accent1}30`,
            borderRadius: 20,
          }}
        >
          <span style={{ fontSize: 40 }}>🤔</span>
          <div style={{ fontSize: 38, color: 'rgba(255,255,255,0.85)', marginTop: 10, fontWeight: 500 }}>
            {p.reflection}
          </div>
        </div>
      </div>

      {/* Progress */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: `${interpolate(frame, [0, 450], [0, 100], { extrapolateRight: 'clamp' })}%`,
          height: 4,
          background: `linear-gradient(90deg, ${accent1}, ${accent2})`,
        }}
      />
    </AbsoluteFill>
  );
};
